import { useMemo, useState } from 'react';
import { GrowthDashboardTopBarActions, KeywordRankingsView } from '@elva/ui';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { useKeywordBrief, useKeywordMutations, useKeywords } from '../hooks/useKeywords';

export function KeywordRankingsPage() {
  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const [category, setCategory] = useState('All');
  const [page, setPage] = useState(1);
  const [expandedKeywordId, setExpandedKeywordId] = useState<string | null>(null);

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } = useKeywords({
    category,
    page,
  });

  const {
    data: brief,
    isFetching: briefLoading,
  } = useKeywordBrief(expandedKeywordId ?? '', Boolean(expandedKeywordId));
  const { regenerate } = useKeywordMutations();

  const items = useMemo(() => {
    if (!data?.items) return undefined;
    return data.items.map((row) => {
      if (row.id !== expandedKeywordId || !brief?.detail) return row;
      return { ...row, detail: brief.detail };
    });
  }, [brief?.detail, data?.items, expandedKeywordId]);

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
      isEmpty={data !== undefined && data.items.length === 0 && page === 1}
    >
      <KeywordRankingsView
        {...viewProps({
          items,
          stats: data?.stats,
          measuredFrom: data?.measuredFrom,
          summary: data?.summary,
          pagination: data?.pagination,
          loading: isFetching || briefLoading || regenerate.isPending,
          category,
          onCategoryChange: (next: string) => {
            setCategory(next);
            setPage(1);
            setExpandedKeywordId(null);
          },
          onPageChange: setPage,
          onKeywordExpand: setExpandedKeywordId,
          keywordDetailLoadingId: briefLoading ? expandedKeywordId : null,
          keywordBriefGenerating: Boolean(brief?.generating),
          onRegenerateBrief: expandedKeywordId
            ? () => regenerate.mutate(expandedKeywordId)
            : undefined,
        })}
      />
    </AsyncBoundary>
  );
}
